import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const sections = [
  {
    title: 'Subscription Model',
    content: [
      {
        text: 'Reano AI operates on a subscription-based pricing model.',
      },
      {
        list: ['Plans are billed in advance', 'Access is provided for the paid period'],
      },
    ],
  },
  {
    title: 'Free Trial',
    content: [
      {
        text: 'Free trial may be provided.',
      },
      {
        list: [
          'No charges during trial period',
          'After trial, subscription begins automatically (if applicable)',
        ],
      },
    ],
  },
  {
    title: 'Cancellation',
    content: [
      {
        text: 'You may cancel your subscription:',
      },
      {
        list: [
          'At any time before the next billing cycle',
          'Via dashboard or by contacting support',
        ],
      },
    ],
  },
  {
    title: 'Refund Policy',
    content: [
      {
        highlight: 'All payments are non-refundable.',
      },
      {
        text: 'No refunds for:',
        list: ['Partial usage', 'Unused subscription period', 'Downgrades'],
      },
    ],
  },
  {
    title: 'Exceptions',
    content: [
      {
        text: 'Refunds may be considered only if:',
      },
      {
        list: ['Duplicate payment', 'Technical failure preventing service usage'],
      },
    ],
  },
  {
    title: 'Effect of Cancellation',
    content: [
      {
        list: [
          'Access continues until end of billing cycle',
          'No further charges after cancellation',
          'Data may be deleted after account closure',
        ],
      },
    ],
  },
  {
    title: 'Price Changes',
    content: [
      {
        text: 'Pricing may change with prior notice.',
      },
      {
        text: 'Continued use after notice constitutes acceptance of updated pricing.',
      },
    ],
  },
];

export default function RefundPolicy() {
  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB' }}>

      <div style={{ background: 'linear-gradient(135deg, #479EC3 0%, #3B88A9 100%)', padding: '48px 0 52px' }}>
        <div className="container">
          <Link to="/" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontSize: 13.5, fontWeight: 500, color: 'rgba(255,255,255,0.8)',
            textDecoration: 'none', marginBottom: 28, transition: 'color .15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 3L5 8l5 5"/>
            </svg>
            Back to Home
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              fontSize: 11.5, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)', borderRadius: 100,
              padding: '4px 14px', marginBottom: 18,
            }}>
              Legal
            </div>
            <h1 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-.03em', color: '#fff', marginBottom: 12 }}>
              Cancellation &amp; Refund Policy
            </h1>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.78)' }}>
              Effective date: <strong style={{ color: '#fff' }}>1 April 2026</strong>&nbsp;·&nbsp;Reano AI Technologies
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container" style={{ padding: '64px 28px 96px' }}>
        <div style={{ maxWidth: 780 }}>

          {sections.map((sec, i) => (
            <motion.div
              key={sec.title}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1 + i * 0.04 }}
              style={{
                background: '#fff', borderRadius: 16, padding: '32px',
                border: '1px solid #E2E8F0', marginBottom: 16,
              }}
            >
              <h2 style={{
                fontSize: 17, fontWeight: 700, color: '#0F2335',
                letterSpacing: '-.02em', marginBottom: 20,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: 'rgba(71,158,195,0.1)', color: '#479EC3',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 800, flexShrink: 0,
                }}>{String(i + 1).padStart(2, '0')}</span>
                {sec.title}
              </h2>

              {sec.content.map((block, j) => (
                <div key={j} style={{ marginBottom: j < sec.content.length - 1 ? 16 : 0 }}>
                  {block.subtitle && (
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#479EC3', marginBottom: 8 }}>
                      {block.subtitle}
                    </div>
                  )}
                  {block.highlight && (
                    <p style={{ fontSize: 14.5, fontWeight: 600, color: '#0F2335', marginBottom: 8 }}>
                      {block.highlight}
                    </p>
                  )}
                  {block.text && (
                    <p style={{ fontSize: 14.5, color: '#4A6070', lineHeight: 1.75, marginBottom: block.list ? 12 : 0 }}>
                      {block.text}
                    </p>
                  )}
                  {block.list && (
                    <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {block.list.map((item, k) => (
                        <li key={k} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14.5, color: '#4A6070', lineHeight: 1.65 }}>
                          <span style={{
                            width: 6, height: 6, borderRadius: '50%', background: '#479EC3',
                            flexShrink: 0, marginTop: 8,
                          }}/>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
